import { useMemo, useState } from 'react';
import { Progress } from 'antd';
import { useGlobalStore } from '@digiwedge/global-state';

function iso(dt: Date) {
  return new Date(dt).toISOString().slice(0, 19);
}

export function ExportConsents({ registryUrl, siteKey }: { registryUrl: string; siteKey: string }) {
  const now = useMemo(() => new Date(), []);
  const [mode, setMode] = useState<'7d' | '30d' | 'custom'>('30d');
  const [from, setFrom] = useState(() => new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
  const [to, setTo] = useState(() => now);
  const [region, setRegion] = useState('');
  const [gpc, setGpc] = useState<'any' | '1' | '0'>('any');
  const [format, setFormat] = useState<'csv' | 'json' | 'jsonl'>('csv');

  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);

  const run = async () => {
    setDownloading(true);
    setProgress(null);
    const token = useGlobalStore.getState().token ?? undefined;
    const u = new URL(
      `${registryUrl}/admin/analytics/sites/by-key/${encodeURIComponent(siteKey)}/consents/export`,
    );
    u.searchParams.set('format', format);
    if (mode === 'custom') {
      u.searchParams.set('from', new Date(from).toISOString());
      u.searchParams.set('to', new Date(to).toISOString());
    } else {
      u.searchParams.set('range', mode);
    }
    if (region) u.searchParams.set('region', region);
    if (gpc !== 'any') u.searchParams.set('gpc', gpc);
    const r = await fetch(u.toString(), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) {
      alert(`Export failed: ${r.status}`);
      setDownloading(false);
      return;
    }
    const total = Number(r.headers.get('content-length') || 0) || null;
    const reader = r.body?.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    if (reader) {
      // Stream with progress
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          chunks.push(value);
          received += value.byteLength;
          if (total) setProgress(Math.round((received / total) * 100));
        }
      }
      const blob = new Blob(chunks, {
        type: format === 'csv' ? 'text/csv' : 'application/json',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download =
        format === 'csv'
          ? `consents_${siteKey}.csv`
          : format === 'jsonl'
            ? `consents_${siteKey}.jsonl`
            : `consents_${siteKey}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    }
    setDownloading(false);
  };

  return (
    <section style={{ marginTop: 12, padding: 12, border: '1px solid #eee', borderRadius: 12 }}>
      <h3>Export consents</h3>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <label>
          Range
          <select value={mode} onChange={(e) => setMode(e.target.value as '7d' | '30d' | 'custom')}>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom</option>
          </select>
        </label>
        {mode === 'custom' && (
          <>
            <label>
              From
              <input
                type="datetime-local"
                value={iso(from)}
                onChange={(e) => setFrom(new Date(e.target.value))}
              />
            </label>
            <label>
              To
              <input
                type="datetime-local"
                value={iso(to)}
                onChange={(e) => setTo(new Date(e.target.value))}
              />
            </label>
          </>
        )}
        <label>
          Region
          <input
            placeholder="eea|uk|row or blank"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          />
        </label>
        <label>
          GPC
          <select value={gpc} onChange={(e) => setGpc(e.target.value as 'any' | '1' | '0')}>
            <option value="any">Any</option>
            <option value="1">GPC on</option>
            <option value="0">GPC off</option>
          </select>
        </label>
        <label>
          Format
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as 'csv' | 'json' | 'jsonl')}
          >
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
            <option value="jsonl">JSONL (stream)</option>
          </select>
        </label>
        <button onClick={run} disabled={downloading}>
          {downloading ? 'Downloading…' : 'Download'}
        </button>
      </div>
      {downloading && (
        <div style={{ marginTop: 8, maxWidth: 320 }}>
          <Progress
            percent={progress ?? 0}
            status={progress === null ? 'active' : undefined}
            showInfo={progress !== null}
          />
        </div>
      )}
      <p style={{ marginTop: 6, color: '#666' }}>
        CSV columns: timestamp, site_key, version, gpc, region, source, analytics, marketing,
        functional, outcome
      </p>
    </section>
  );
}
